import { get, writable } from 'svelte/store';
import { ConfigStore } from '../lib/store/config';
import { UserStateHelper } from '../lib/helpers/user-state-helper';
import {
  initialVoiceState,
  conversationMode,
  hasVoice,
  canSwitchModes,
  type WidgetConversationMode,
  type VoiceController,
  type VoiceState,
} from './types';

export const voiceMode = writable(false);
export const voiceState = writable<VoiceState>(initialVoiceState());
export const transcriptVisible = writable(false);

// Capture the classic embed script's URL while its IIFE is executing, not on click.
const scriptUrl =
  (document.currentScript as HTMLScriptElement | null)?.src ||
  [...document.scripts].find((script) =>
    /\/cs-search\.js(?:[?#]|$)/.test(script.src)
  )?.src;
let controller: VoiceController | undefined;
let loading: Promise<VoiceController> | undefined;
let generation = 0;

async function loadController(): Promise<VoiceController> {
  if (controller) return controller;
  if (!loading) {
    const config = get(ConfigStore);
    const url =
      config.widget?.live?.moduleUrl ||
      (import.meta.env.DEV
        ? `${import.meta.env.BASE_URL}src/voice/runtime.ts`
        : new URL(
            'cs-voice.js',
            scriptUrl ||
              new URL(
                `${(config.assetBaseUrl || config.apiBaseUrl || location.origin).replace(/\/$/, '')}/`
              )
          ).href);
    loading = import(/* @vite-ignore */ url)
      .then((module) => {
        controller = module.createVoiceController((state: VoiceState) =>
          voiceState.set(state)
        );
        return controller!;
      })
      .catch((error) => {
        loading = undefined;
        throw error;
      });
  }
  return loading;
}

export async function startVoice() {
  const config = get(ConfigStore);
  const capabilities = config.widget?.live;
  if (
    !get(voiceMode) ||
    !capabilities ||
    !hasVoice(capabilities) ||
    get(voiceState).active
  )
    return;
  const attempt = ++generation;
  voiceState.update((s) => ({
    ...s,
    active: true,
    status: 'loading',
    error: '',
    transcript: [],
  }));
  try {
    const runtime = await loadController();
    if (attempt !== generation || !get(voiceMode)) return;
    await runtime.start({
      apiBaseUrl: config.apiBaseUrl || location.origin,
      tenant: config.serverConfig?.tenant_id,
      config: config.serverConfig?.resolved_id || config.serverConfig?.id,
      agent: config.serverConfig?.agent_id,
      capabilities,
      metadata: {
        user_id: UserStateHelper.getUserId(),
        user_tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        url: config.customAttributes?.url || location.href,
        attributes: config.customAttributes?.attributes,
        flags: config.customAttributes?.flags,
      },
    });
  } catch {
    if (attempt === generation)
      voiceState.update((s) => ({
        ...s,
        active: false,
        status: 'idle',
        error: 'load_error',
      }));
  }
}

export function stopVoice() {
  ++generation;
  controller?.stop();
  voiceState.update((s) => ({
    ...s,
    active: false,
    connected: false,
    status: 'idle',
  }));
}

export function switchVoiceMode() {
  if (!canSwitchModes(get(ConfigStore).widget?.live)) return;
  if (get(voiceMode)) {
    // The user must stop the call before returning to text.
    if (get(voiceState).active) return;
    stopVoice();
    voiceMode.set(false);
  } else {
    voiceMode.set(true);
    void startVoice();
  }
}

export const voiceControls = {
  readAudioLevel: () => controller?.readAudioLevel() ?? 0,
  toggle: async (source: Parameters<VoiceController['toggle']>[0]) => {
    try {
      // Keep active-call controls in the click gesture (screen sharing on Safari).
      if (controller) return controller.toggle(source);
      const runtime = await loadController();
      await runtime.toggle(source);
    } catch {
      voiceState.update((s) => ({ ...s, error: 'load_error' }));
    }
  },
  refreshDevices: async (kind?: 'audioinput' | 'videoinput') => {
    try { await (await loadController()).refreshDevices(kind); }
    catch { voiceState.update((s) => ({ ...s, error: 'load_error' })); }
  },
  selectDevice: async (kind: 'audioinput' | 'videoinput', id: string) => {
    try { await (await loadController()).selectDevice(kind, id); }
    catch { voiceState.update((s) => ({ ...s, error: 'load_error' })); }
  },
  startAudio: () => controller?.startAudio(),
  attachPreview: (element: HTMLVideoElement, source: 'camera' | 'screen') =>
    controller?.attachPreview(element, source),
};

// Apply the configured starting view only when the mode setting changes.
// Branding/config refreshes and minimising must preserve the user's chosen view/call.
let configuredMode: WidgetConversationMode | undefined;
ConfigStore.subscribe((config) => {
  const mode = conversationMode(config.widget?.live);
  if (mode === configuredMode) return;
  if (configuredMode !== undefined) stopVoice();
  configuredMode = mode;
  voiceMode.set(mode === 'voice-first' || mode === 'voice-only');
});
window.addEventListener('pagehide', stopVoice);
